import { useEffect, useRef, useState } from 'react';

const SCROLL_SPEED = 50; // pixels per second

/**
 * Custom hook for marquee scroll animation
 * Handles animation frame management, reduced motion preference, and pause on hover
 */
export const useMarqueeAnimation = () => {
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Animation loop
  useEffect(() => {
    if (prefersReducedMotion || !scrollRef.current || !contentRef.current) return;

    let animationId;
    let position = 0;
    const contentWidth = contentRef.current.offsetWidth;
    
    const animate = () => {
      if (!isPaused) {
        position -= SCROLL_SPEED / 60; // 60fps
        if (position <= -contentWidth / 2) {
          position = 0;
        }
        if (scrollRef.current) {
          scrollRef.current.style.transform = `translateX(${position}px)`;
        }
      }
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [isPaused, prefersReducedMotion]);

  const pause = () => setIsPaused(true);
  const resume = () => setIsPaused(false);

  return {
    scrollRef,
    contentRef,
    prefersReducedMotion,
    pause,
    resume,
  };
};
